import React from "react";

/**
 * The read-only "Patient Info" block — the identity strip plus DOB / address /
 * email / phone — as shown at the top of the insurance policy form.
 *
 * Extracted from PatientPolicyInfoFormModal so the Insurance Requests composer can
 * show the SAME card on its policy step without a second copy of the markup, the
 * DOB/address normalising, or the CSS class contract. Styling still comes from
 * TreatmentPlans/style.css (`.patient-policy-subject-card*`), untouched.
 *
 * `sectionHeading` is exported alongside it because the policy form uses the same
 * heading treatment for its other sections ("Member Info", "Insurance Plan") — one
 * definition rather than two that can drift apart.
 */

export function formatPatientDOB(patient) {
  const birthDate =
    patient && patient.birth_date
      ? patient.birth_date
      : patient && patient.date_of_birth
        ? patient.date_of_birth
        : null;
  if (!birthDate) return "—";
  if (typeof birthDate === "string") return birthDate.split("T")[0];
  return String(birthDate);
}

export function formatAddress(patient) {
  if (!patient) return "—";
  const parts = [patient.address, patient.city, patient.state, patient.zip_code].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

export function sectionHeading(text) {
  return (
    <div className="patient-policy-section-heading">
      <span className="patient-policy-section-heading__text">{text}</span>
      <span className="patient-policy-section-heading__rule" aria-hidden />
    </div>
  );
}

function metadataField(label, value) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="patient-policy-meta-label">{label}</div>
      <div className="patient-policy-meta-value">{value || "—"}</div>
    </div>
  );
}

/**
 * @param {Object} patient  a patient record — the PatientList search row already
 *   carries every field used here (first/last name, birth_date, address, city,
 *   state, zip_code, email, phone), so no extra fetch is needed.
 * @param {boolean} [heading=true] render the "Patient Info" section heading above
 *   the card. False when the host already labels the block.
 */
export default function PatientInfoCard({ patient, heading = true }) {
  const info = patient || {};
  const fullName = [info.first_name, info.last_name].filter(Boolean).join(" ") || "Unknown patient";

  return (
    <>
      {heading ? sectionHeading("Patient Info") : null}
      <div className="patient-policy-subject-card">
        <div className="patient-policy-subject-card__identity">
          <div className="patient-policy-subject-card__eyebrow">Patient</div>
          <div className="patient-policy-subject-card__name">{fullName}</div>
        </div>
        <div className="patient-policy-subject-card__metadata">
          {metadataField("DOB", formatPatientDOB(info))}
          {metadataField("Address", formatAddress(info))}
          {metadataField("Email", info.email)}
          {metadataField("Phone", info.phone)}
        </div>
      </div>
    </>
  );
}
